import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  ClipboardList,
  Settings,
  ShieldCheck,
  Pill,
  LogOut,
  ChevronDown,
  MessageSquareWarning,
  AlertTriangle,
  Baby,
  CalendarDays,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useActiveRole } from "@/hooks/useActiveRole";
import { useFeatureVisibility } from "@/hooks/useFeatureVisibility";
import { supabase } from "@/integrations/supabase/client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

const allNavItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", roles: ["patient", "carer", "admin", "parent"], feature: null as null | "medications" | "allergies" | "children" | "diary" },
  { to: "/patients", icon: Users, label: "People", roles: ["carer", "admin"], feature: null },
  { to: "/enter-medications", icon: Pill, label: "Enter Medications", roles: ["patient", "carer", "admin"], feature: "medications" as const },
  { to: "/enter-allergies", icon: AlertTriangle, label: "Enter Allergies", roles: ["patient", "carer", "admin"], feature: "allergies" as const },
  { to: "/children", icon: Baby, label: "Children", roles: ["patient", "carer", "parent", "admin"], feature: "children" as const },
  { to: "/appointments", icon: CalendarDays, label: "Appointments", roles: ["patient", "carer", "parent"], feature: "diary" as const },
  { to: "/history", icon: ClipboardList, label: "History", roles: ["patient", "carer", "admin"], feature: "medications" as const },
  { to: "/alert-admin", icon: MessageSquareWarning, label: "Alert Admin", roles: ["patient", "carer", "parent"], feature: null },
  { to: "/settings", icon: Settings, label: "Settings", roles: ["patient", "carer", "admin", "parent"], feature: null },
];

const adminItems = [
  { to: "/admin", icon: ShieldCheck, label: "Admin" },
];

const roleLabels: Record<string, string> = {
  patient: "Person",
  carer: "Carer",
  admin: "Admin",
  parent: "Parent",
};

export function DesktopSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeRole, availableRoles, setActiveRole } = useActiveRole();
  const { showMedications, showAllergies, showChildren, showDiary } = useFeatureVisibility();

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  const canSwitch = availableRoles.length > 1;

  return (
    <aside className="flex flex-col w-64 border-r bg-sidebar text-sidebar-foreground">
      <div className="px-6 py-5 border-b border-sidebar-border">
        <div className="flex items-center gap-2.5">
          <Pill className="h-7 w-7 text-sidebar-primary" />
          <span className="text-xl font-bold tracking-tight text-sidebar-primary-foreground">
            MedCarer
          </span>
        </div>

        {/* Role badge / switcher */}
        <div className="mt-2 min-h-[22px]">
          {canSwitch ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1.5 text-sm font-medium text-sidebar-foreground/70 hover:text-sidebar-foreground transition-colors">
                  <Badge variant="secondary" className="text-xs capitalize">
                    {roleLabels[activeRole] || activeRole}
                  </Badge>
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-40">
                {availableRoles.map((role) => (
                  <DropdownMenuItem
                    key={role}
                    onClick={() => setActiveRole(role)}
                    className={cn("capitalize", role === activeRole && "font-bold")}
                  >
                    {roleLabels[role] || role}
                    {role === activeRole && " ✓"}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Badge variant="secondary" className="text-xs capitalize">
              {roleLabels[activeRole] || activeRole}
            </Badge>
          )}
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1" aria-label="Main navigation">
        {allNavItems.filter((item) => {
          if (!item.roles.includes(activeRole)) return false;
          if (item.feature === "medications" && !showMedications) return false;
          if (item.feature === "allergies" && !showAllergies) return false;
          if (item.feature === "children" && !showChildren) return false;
          if (item.feature === "diary" && !showDiary) return false;
          return true;
        }).map((item) => {
          const isActive = location.pathname.startsWith(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              aria-current={isActive ? "page" : undefined}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                isActive
                  ? "bg-sidebar-accent text-sidebar-primary"
                  : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              )}
            >
              <item.icon className="h-5 w-5" aria-hidden="true" />
              {item.label}
            </Link>
          );
        })}

        {activeRole === "admin" && (
          <>
            <div className="pt-4 pb-2">
              <span className="px-3 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/50">
                Admin
              </span>
            </div>
            {adminItems.map((item) => {
              const isActive = location.pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                    isActive
                      ? "bg-sidebar-accent text-sidebar-primary"
                      : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Link>
              );
            })}
          </>
        )}
      </nav>

      <div className="px-3 py-4 border-t border-sidebar-border">
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-sidebar-foreground hover:text-sidebar-accent-foreground"
          onClick={handleSignOut}
        >
          <LogOut className="h-5 w-5" />
          Sign out
        </Button>
      </div>
    </aside>
  );
}
